import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface Props {
  src: string;
  alt: string;
}

/**
 * A help screenshot that opens large when clicked. (#534) The screenshots are 1,500 to
 * 3,400 pixels wide and shown at the column's width, about 700, where their text cannot be
 * read. Opened, one fits the window on a wide screen; on a phone, where fitting it would make
 * it no bigger, it shows near its own size and scrolls (see .image-zoom in App.css).
 */
const ZoomableImage: React.FC<Props> = ({ src, alt }) => {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button ref={opener} type="button" className="zoomable-image" aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <img src={src} alt={alt} />
      </button>
      {open && <ImageZoom src={src} alt={alt} returnFocusTo={opener} onClose={() => setOpen(false)} />}
    </>
  );
};

/* Built as SmartScreenHelp's dialog is: focus moves in and goes back to the image on close,
   the page behind stays put, Escape closes, and so does a click that both starts and ends
   on the backdrop. Focus goes back to the image's own button, not to whatever had it when
   this opened: Safari does not focus a button that is clicked. A press on the backdrop
   keeps focus where it is: it would otherwise go to the page, and Escape and the Tab trap
   go with it. */
interface ZoomProps extends Props {
  returnFocusTo: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

const ImageZoom: React.FC<ZoomProps> = ({ src, alt, returnFocusTo, onClose }) => {
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const opener = returnFocusTo.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = overflow;
      opener?.focus({ preventScroll: true });
    };
  }, [returnFocusTo]);

  return (
    <div
      className="image-zoom-overlay"
      onMouseDown={e => {
        pressedBackdrop.current = e.target === e.currentTarget;
        if (pressedBackdrop.current) e.preventDefault();
      }}
      onClick={e => { if (pressedBackdrop.current && e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialog} tabIndex={-1}
        className="image-zoom" role="dialog" aria-modal="true" aria-label={alt}
        onKeyDown={e => {
          if (e.key === 'Escape') onClose();
          // The close button is the one thing in here to reach.
          if (e.key === 'Tab') { e.preventDefault(); close.current?.focus(); }
        }}
      >
        <button ref={close} type="button" className="image-zoom-close" aria-label="Close" onClick={onClose}>
          <X size={20} />
        </button>
        <img src={src} alt={alt} />
      </div>
    </div>
  );
};

export default ZoomableImage;
